import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronLeft, ChevronRight, History, Home } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { getRecentlyViewedProjects } from '@/lib/recentlyViewed';
import { optimizedImageUrl } from '@/lib/imageOptimizer';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPriceRange } from '@/lib/formatPrice';
import { trackLandingEvent } from '@/lib/landingAnalytics';

const PLACEHOLDER = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=400';

export function ViewedProjectsRow() {
  const ids = getRecentlyViewedProjects();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const { displayCurrency, rate } = useCurrency();

  const { data: projects } = useQuery({
    queryKey: ['landing-viewed-projects', ids.join(',')],
    enabled: ids.length > 0,
    queryFn: async () => {
      const { data } = await supabase
        .from('projects')
        .select('*, project_images(*)')
        .in('id', ids);
      const map = new Map((data || []).map((p: any) => [p.id, p]));
      return ids.map(id => map.get(id)).filter(Boolean);
    },
  });

  useEffect(() => {
    if (!projects || projects.length === 0 || !sectionRef.current) return;
    const el = sectionRef.current;
    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && e.intersectionRatio >= 0.5) {
          trackLandingEvent({
            event_type: 'viewed_projects_impression',
            metadata: { count: projects.length },
            dedupKey: 'viewed_projects',
          });
          obs.disconnect();
          break;
        }
      }
    }, { threshold: [0.5] });
    obs.observe(el);
    return () => obs.disconnect();
  }, [projects]);

  if (!ids.length || !projects?.length) return null;

  const scroll = (dir: 'left' | 'right') => {
    if (!ref.current) return;
    const w = ref.current.clientWidth * 0.6;
    ref.current.scrollBy({ left: dir === 'left' ? -w : w, behavior: 'smooth' });
  };

  const handleClick = (p: any, position: number) => {
    trackLandingEvent({
      event_type: 'viewed_project_click',
      entity_type: 'project',
      entity_id: p.id,
      metadata: { position },
    });
    navigate(`/projects/${p.slug || p.id}`);
  };

  return (
    <section ref={sectionRef} className="container py-10">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-primary" />
          <h2 className="font-display text-xl md:text-2xl font-bold">Recently Viewed Projects</h2>
        </div>
        <div className="flex gap-2">
          <button onClick={() => scroll('left')} aria-label="Scroll left" className="h-8 w-8 rounded-full border bg-card flex items-center justify-center hover:bg-muted">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={() => scroll('right')} aria-label="Scroll right" className="h-8 w-8 rounded-full border bg-card flex items-center justify-center hover:bg-muted">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div ref={ref} className="overflow-x-auto scrollbar-hide scroll-smooth">
        <div className="flex gap-4">
          {projects.map((p: any, idx: number) => {
            const name = typeof p.name === 'object' ? p.name?.en || '' : p.name || '';
            const img = p.project_images?.find((i: any) => i.is_primary)?.url || p.project_images?.[0]?.url;
            const src = optimizedImageUrl(img || PLACEHOLDER, { width: 400, height: 300, quality: 75 });
            const priceRange = p.price_min ? formatPriceRange(p.price_min, p.price_max, p.currency || 'ETB', displayCurrency, rate) : '';
            return (
              <div key={p.id} onClick={() => handleClick(p, idx)}
                className="flex-shrink-0 w-[260px] rounded-xl border bg-card overflow-hidden hover:shadow-lg cursor-pointer group">
                <div className="relative aspect-[4/3]">
                  <img src={src} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-3 space-y-1">
                  {priceRange && <div className="font-bold text-primary text-sm">{priceRange}</div>}
                  <div className="font-semibold text-sm leading-tight truncate">{name}</div>
                  <div className="text-xs text-muted-foreground truncate">{p.city}{p.sub_city ? `, ${p.sub_city}` : ''}</div>
                  <div className="flex items-center gap-3 pt-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Home className="h-3 w-3" /> {p.status?.replace('_', ' ') || 'Apartments'}</span>
                    {p.total_units && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" /> {p.available_units || p.total_units} units</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
